import React, { FC, useState } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Table } from '../table/Table';
import { TablesNames } from '../../store/tables/tables-context';
import { OwnerDetailPanel } from '../merchants/MerchantPage/OwnerDetailPanel';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { OwnerModalForm } from '../merchants/MerchantPage/OwnerModalForm';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '../ButtonComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { IOwners } from '../../interfaces/merchant';
import clsx from 'clsx';

interface Props {
	appOwnerData?: IOwners[],
	setAppOwnerData?: (value: IOwners[]) => void,
	formEditStatus: boolean;
	showActions?: boolean;
	showAddButton: boolean
}

export const ApplicationOwner: FC<Props> = (props) => {
	const { appOwnerData, setAppOwnerData, formEditStatus, showActions, showAddButton } = props;
	const { t } = useTranslation();

	const [ownerModalState, setModalOwnerState] = useState<{ isOpen: boolean, ownerIndex?: number }>({
    isOpen: false,
    ownerIndex: null,
  });
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const ownerData = appOwnerData?.find((el, index) => index === ownerModalState.ownerIndex);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const closeOwnerModal = (): void => setModalOwnerState({ isOpen: false, ownerIndex: null });

	const addNewOwnerBtn = (
		<ButtonComponent
			buttonName="Add"
			additionalClass="btn-sm"
			onButtonClick={(): void => setModalOwnerState({ isOpen: true })}
			isDisabled={!formEditStatus}
		/>
	);

	const handleError = (): void => {
		closeOwnerModal();
		setModalState({
      isOpen: true,
      modalTitle: 'Edit owner`s information',
      content: (
        <div className="fs-6">
          Failed saving owner`s information
        </div>
      ),
      confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
      minHeight: 100,
      onConfirm: closeModal,
      onClose: closeModal
    });
	};

	const handleDelete = (ownerId?: string): void => {
		setModalState({
      isOpen: true,
      modalTitle: 'Delete owner`s information',
      modalText: 'Attention! Are you sure, that you want to delete owner`s information? This action cannot be undone.',
      onConfirm: () => {
	      const newAppOwnerData = [...appOwnerData];
	      const selectedOwnerIndex = newAppOwnerData?.findIndex(el => el?.id === ownerId);
	      newAppOwnerData.splice(selectedOwnerIndex, 1);
	      setAppOwnerData(newAppOwnerData);
        closeModal();
      },
      onCancel: closeModal,
      onClose: closeModal,
      backdrop: 'static',
    });
	};

	const handleSubmit = (data: IOwners): void => {
		if (ownerModalState.ownerIndex !== null && ownerModalState.ownerIndex !== undefined) {
			if (ownerData?.id) {
				data.id = ownerData?.id;
			}
			const newAppOwnerData = [...appOwnerData];
			newAppOwnerData.splice(ownerModalState.ownerIndex, 1, data);
			setAppOwnerData(newAppOwnerData);
		} else {
			setAppOwnerData([...appOwnerData, data]);
		}
	};

	const rowActions = (rowData): JSX.Element => {
		return (
			<div className="d-flex align-items-center justify-content-center">
				<div
					className={clsx('d-flex align-items-center justify-content-center me-5', formEditStatus ? 'cursor-pointer' : 'cursor-not-allowed')}
					onClick={(): void => formEditStatus && setModalOwnerState({ isOpen: true, ownerIndex: rowData?.index })}
				>
					<FontAwesomeIcon icon={faPenToSquare} className="text-muted" />
					<span className="text-muted fs-7 fw-bolder ms-2">
						Edit
					</span>
				</div>
				<div
					className={clsx('d-flex align-items-center justify-content-center', formEditStatus ? 'cursor-pointer' : 'cursor-not-allowed')}
					onClick={(): void => formEditStatus && handleDelete(rowData?.original?.id) }
				>
					<FontAwesomeIcon icon={faTrashCan} className="text-muted" />
					<span className="text-muted fs-7 fw-bolder ms-2">
						Delete
					</span>
				</div>

			</div>
		);
	};

	return (
		<>
			<SectionWrapper
				title={t('MERCHANT.OWNER')}
				sectionBodyAdditionalClass="pb-0"
				pageLoading={false}
				isCardBody
				headerAdditionalBlock={showAddButton && addNewOwnerBtn}
				sectionAdditionalClass="mb-10"
			>
				<Table
					name={TablesNames.merchantOwnerTableData}
					data={appOwnerData || []}
					isLoading={false}
					renderDetailPanel={OwnerDetailPanel}
					enableRowActions={showActions}
					rowActions={rowActions}
					positionActionsColumn='last'
					actionColumnsSize={100}
				/>

			</SectionWrapper>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={ownerModalState.isOpen}
				onClose={closeOwnerModal}
				size="md"
				backdrop="static"
				modalTitle={ownerModalState.ownerIndex ? 'Edit owner' : 'Add new owner'}
				enableOverflow={false}
				content={<OwnerModalForm
					ownerId={ownerModalState.ownerIndex}
					ownerData={ownerData}
					onClose={closeOwnerModal}
					onError={handleError}
					onSubmit={handleSubmit}
				/>}
			/>

		</>
	);
};
